import { ContactInfo } from '../../../../types';

/**
 * Contact validation interface.
 * Abstracts the validation logic from the data source.
 * This allows us to change how contacts are stored/fetched without changing validation consumers.
 */
export type ContactValidator = {
  isValidPhone: (phone: string) => boolean;
  normalizePhone: (phone: string) => string;
};

/**
 * Normalize phone number for comparison.
 * Removes all formatting characters and ensures leading +.
 * Examples:
 *   '+1(555)123-4567' -> '+15551234567'
 *   '15551234567' -> '+15551234567'
 *   '+1 555 123 4567' -> '+15551234567'
 */
export const normalizePhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
};

/**
 * Creates a contact validator from a list of phone contacts.
 * This is the current implementation using CustomerDetail contacts.
 * Can be replaced with different data sources in the future (e.g., API-based validation).
 */
export const createContactValidator = (
  primaryPhone: ContactInfo | null,
  additionalPhones: ContactInfo[],
): ContactValidator => {
  const allPhones = [primaryPhone?.identifier, ...additionalPhones.map((p) => p.identifier)].filter(
    Boolean,
  ) as string[];

  const normalizedPhones = new Set(allPhones.map(normalizePhoneNumber));

  return {
    isValidPhone: (phone: string): boolean => {
      const normalized = normalizePhoneNumber(phone);
      return normalizedPhones.has(normalized);
    },
    normalizePhone: normalizePhoneNumber,
  };
};
